/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import ModuleRelinker from './ModuleRelinker';
//@import com/ibm/bi/platform/moser/common/relink/ModuleRelinker (loadtime) // collectItemsIdentifiers, superclass, scoreCandidate

/**
 * Relinker for an Embedded Module.
 * @author  PASCALLacombe
 */
export default class EmbeddedModuleRelinker extends ModuleRelinker{

	qsIds = null;

	newQsIds = null;

	qsAndQiIds = null;

	newQsAndQiIds = null;

	qsIdRemap = null;



	/**
	 * Relink a source in a module
	 * @param {UseSpec} useSpec  : the useSpec to relink
	 * @param {UseSpecType} newSrcType  : the new source type (package, module, file, ...etc)
	 * @param {String} newSrcStoreId  : the new source module store id
	 * @param  newSrcAncestors: the ancestors of the useSpec
	 * @param  objectFactory: factory to create new module objects
	 * @return {UseSpec}  newly created UseSpec for the relinked source
	 */
	relinkSource$0(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory) {
		var newUseSpec = this._doRelinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory);
		newUseSpec.setImports("*");
		return newUseSpec;
	}

	/**
	 * Relink identifiers in a module that refer to a particular useSpec so they use identifiers from newSrcModule.
	 * @param {UseSpec} useSpec  : the useSpec to relink identifiers for
	 * @override
	 */
	relinkIdentifiers(useSpec) {
		this.qsIds = [];
		this.newQsIds = [];
		this.qsAndQiIds = {};
		this.newQsAndQiIds = {};
		if (!(this._containsDrillGroups())) {
			return;
		}
		EmbeddedModuleRelinker._collectQsAndQiIds(this.module, this.qsIds, this.qsAndQiIds);
		EmbeddedModuleRelinker._collectQsAndQiIds(this.newSrcModule, this.newQsIds, this.newQsAndQiIds);
		if (!(this._collectIdsFromDrillGroups())) {
			return;
		}
		this._buildQsRemap();
		this._relinkDrillGroups();
	}

	/**
	 * Relink a list of idForExpression's, return null of id's that cannot be remapped.
	 * @param {Array} expressions
	 * @return {Array}  A list of relinked idForExpressions, in the same order as provided.
	 */
	relinkIdForExpressions(expressions) {
		this.qsIds = [];
		this.newQsIds = [];
		this.qsAndQiIds = {};
		this.newQsAndQiIds = {};
		if (!expressions || expressions.length==0) {
			return [];
		}
		EmbeddedModuleRelinker._collectQsAndQiIds(this.module, this.qsIds, this.qsAndQiIds);
		EmbeddedModuleRelinker._collectQsAndQiIds(this.newSrcModule, this.newQsIds, this.newQsAndQiIds);
		if (!(this._collectIdsFromExpressions(expressions))) {
			return expressions;
		}
		this._buildQsRemap();
		var result = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = expressions, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var expression = __exp_enFor0[__i_enFor0];
			result.push(this._relinkExpression(expression));
		}
		return result;
	}

	/**
	 * Build a map of all QuerySubject identifiers and for each one build a list of all identifiers they have.
	 * @param {Module} m
	 * @param {Array} querySubjectIDs
	 * @param {Map} querySubjectQueryItemIDs
	 */
	static _collectQsAndQiIds(m, querySubjectIDs, querySubjectQueryItemIDs) {
		if (!m) {
			return;
		}
		var querySubjects = m.basicGetQuerySubject();
		if (querySubjects) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				var id = qs.getIdentifier();
				var idLower = id.toLowerCase();
				querySubjectIDs.push(id);
				var qiIds = ModuleRelinker._collectItemsIdentifiers(qs.basicGetItem());
				var existingIds = querySubjectQueryItemIDs[idLower];
				if (existingIds) {
					Array.prototype.push.apply(existingIds, qiIds);
				} else {
					querySubjectQueryItemIDs[idLower] = qiIds;
				}
			}
		}
	}

	/**
	 * From a list of idForExpression's, build a map of all QuerySubject identifiers and for each one build a list of all identifiers they have.
	 * @param {Array} expressions
	 * @return {boolean}  True if there is an idForExpression to fix
	 */
	_collectIdsFromExpressions(expressions) {
		var found = false;
		for (var __i_enFor0 = 0, __exp_enFor0 = expressions, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var expression = __exp_enFor0[__i_enFor0];
			var fmBeginPos = expression.indexOf("[");
			if (fmBeginPos != -1) {
				continue;
			}
			var splits = expression.split(new RegExp("\\."));
			if (splits.length < 2) {
				continue;
			}
			found = true;
			var qsId = splits[0];
			var qsIdLower = qsId.toLowerCase();
			var qiId = splits[1];
			var existingIds = this.qsAndQiIds[qsIdLower];
			if (!existingIds) {
				this.qsIds.push(qsId);
				existingIds = [];
				this.qsAndQiIds[qsIdLower] = existingIds;
			}
			if (!(existingIds.indexOf(qiId)> -1)) {
				existingIds.push(qiId);
			}
		}
		return found;
	}

	/**
	 * Check if any drillGroup needs upgrading.
	 * @return {boolean}
	 */
	_containsDrillGroups() {
		var drillGroups = this.module.basicGetDrillGroup();
		if (!drillGroups) {
			return false;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = drillGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var drillGroup = __exp_enFor0[__i_enFor0];
			var segments = drillGroup.basicGetSegment();
			if (drillGroup.isImported() || !segments) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = segments, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var segment = __exp_enFor1[__i_enFor1];
				var ref = segment.getRef();
				var splits = ref.split(new RegExp("\\."));
				if (splits.length != 2) {
					continue;
				}
				return true;
			}
		}
		return false;
	}

	/**
	 * From the list of drillGroups, build a map of all QuerySubject identifiers and for each one build a list of all identifiers they have.
	 * @return {boolean}
	 */
	_collectIdsFromDrillGroups() {
		var drillGroups = this.module.basicGetDrillGroup();
		if (!drillGroups) {
			return false;
		}
		var found = false;
		for (var __i_enFor0 = 0, __exp_enFor0 = drillGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var drillGroup = __exp_enFor0[__i_enFor0];
			var segments = drillGroup.basicGetSegment();
			if (drillGroup.isImported() || !segments) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = segments, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var segment = __exp_enFor1[__i_enFor1];
				var ref = segment.getRef();
				var splits = ref.split(new RegExp("\\."));
				if (splits.length != 2) {
					continue;
				}
				found = true;
				var qsId = splits[0];
				var qsIdLower = qsId.toLowerCase();
				var qiId = splits[1];
				var existingIds = this.qsAndQiIds[qsIdLower];
				if (!existingIds) {
					this.qsIds.push(qsId);
					existingIds = [];
					this.qsAndQiIds[qsIdLower] = existingIds;
				}
				if (!(existingIds.indexOf(qiId)> -1)) {
					existingIds.push(qiId);
				}
			}
		}
		return found;
	}

	/**
	 * Build a map of the QS id re-maps.
	 */
	_buildQsRemap() {
		this.qsIdRemap = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = this.qsIds, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qsId = __exp_enFor0[__i_enFor0];
			this.qsIdRemap[qsId] = this._bestMatch$0(qsId);
		}
	}

	/**
	 * Find the best matching QuerySubject identifier from the newSrcModule Return null is the best match cannot be found.
	 * @param {String} qsId
	 * @return {String}
	 */
	_bestMatch$0(qsId) {
		if (this.newQsIds.length==0) {
			return qsId;
		}
		if (this.newQsIds.length == 1) {
			return this.newQsIds[0];
		}
		var bestScore = 0;
		var bestCandidate;
		var qsIdLower = qsId.toLowerCase();
		var identifiers = this.qsAndQiIds[qsIdLower];
		for (var __i_enFor0 = 0, __exp_enFor0 = this.newQsIds, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var candidate = __exp_enFor0[__i_enFor0];
			var candidateLower = candidate.toLowerCase();
			if (qsIdLower == candidateLower) {
				return candidate;
			}
			var score = ModuleRelinker._scoreCandidate(identifiers, this.newQsAndQiIds[candidateLower]);
			if (score > bestScore) {
				bestCandidate = candidate;
				bestScore = score;
			}
		}
		return bestCandidate;
	}

	/**
	 * Fix a 2++ part expression by replacing the QuerySubject id with the remapped one. Return null if a remapping is not possible.
	 * @param {String} expression  - 2++ part expression to fix
	 * @return {String}  Return the new expression if it can be relinked, otherwise return the original expression
	 */
	_relinkExpression(expression) {
		if (expression == null || expression.length === 0) {
			return null;
		}
		var splits = expression.split(new RegExp("\\."));
		if (splits.length < 2) {
			return null;
		}
		var qsId = splits[0];
		var newQsId = this.qsIdRemap[qsId];
		if (newQsId == null) {
			return null;
		}
		return newQsId + expression.substring(qsId.length);
	}

	/**
	 * Relink DrillGroups in the newSrcModule.
	 */
	_relinkDrillGroups() {
		var drillGroups = this.module.basicGetDrillGroup();
		if (!drillGroups) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = drillGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var drillGroup = __exp_enFor0[__i_enFor0];
			var segments = drillGroup.basicGetSegment();
			if (drillGroup.isImported() || !segments) {
				continue;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = segments, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var segment = __exp_enFor1[__i_enFor1];
				var relinkedRef = this._relinkExpression(segment.getRef());
				if (relinkedRef != null) {
					segment.setRef(relinkedRef);
				}
			}
		}
	}

	relinkSource(a0, a1, a2, a3, a4) {
		var args = arguments;
		return this.relinkSource$0(a0, a1, a2, a3, a4);
	}

	bestMatch(a0) {
		var args = arguments;
		if (args.length == 1 && (a0 == null || typeof a0 === "string")) {
			return this.bestMatch$0(a0);
		}
		return ModuleRelinker.prototype.bestMatch.apply(this, args);
	}

	/**
	 * Constructor
	 * @param {Module} module
	 * @param {Module} newSrcModule
	 */
	//constructor(module, newSrcModule) 
}


// WEBPACK FOOTER //
// ./src/generated/EmbeddedModuleRelinker.js